<template>
  <div :class="ns.e('file-summary')">
    <template v-if="files.length === 0">
      <span :class="ns.e('file-summary-empty')">—</span>
    </template>

    <template v-else-if="files.length === 1">
      <a
        v-if="canDownload(files[0]!)"
        :class="ns.e('file-summary-link')"
        href="javascript:void(0)"
        @click.stop="handleDownload(files[0]!)"
      >
        {{ displayName(files[0]!) }}
      </a>
      <span v-else :class="ns.e('file-summary-text')">
        {{ displayName(files[0]!) }}
      </span>
    </template>

    <template v-else>
      <a
        v-if="canDownload(files[0]!)"
        :class="ns.e('file-summary-link')"
        href="javascript:void(0)"
        @click.stop="handleDownload(files[0]!)"
      >
        {{ displayName(files[0]!) }}
      </a>
      <span v-else :class="ns.e('file-summary-text')">
        {{ displayName(files[0]!) }}
      </span>

      <z-popover
        placement="bottom-start"
        trigger="click"
        :width="280"
        :popper-class="ns.e('file-summary-popover')"
      >
        <template #reference>
          <span :class="ns.e('file-summary-more')" @click.stop>
            +{{ files.length - 1 }}
          </span>
        </template>

        <div :class="ns.e('file-summary-list')">
          <div
            v-for="(file, index) in files"
            :key="`${file.id ?? file.url ?? index}`"
            :class="ns.e('file-summary-item')"
          >
            <a
              v-if="canDownload(file)"
              :class="ns.e('file-summary-link')"
              href="javascript:void(0)"
              @click.stop="handleDownload(file)"
            >
              {{ displayName(file) }}
            </a>
            <span v-else :class="ns.e('file-summary-text')">
              {{ displayName(file) }}
            </span>
          </div>

          <z-button
            v-if="showBatchDownload"
            link
            type="primary"
            size="small"
            :class="ns.e('file-summary-batch')"
            @click.stop="handleBatchDownload"
          >
            {{ t('z.table.uploadCell.batchDownload') }}
          </z-button>
        </div>
      </z-popover>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import ZButton from '@zoryn/components/button'
import ZPopover from '@zoryn/components/popover'
import { useLocale, useNamespace } from '@zoryn/hooks'
import {
  downloadGridFile,
  downloadGridFilesBatch,
  isGridFileDownloadable,
  resolveFileCellDisplayName,
  type GridFileCellMeta,
} from '../utils/file-cell'

const props = withDefaults(
  defineProps<{
    files: GridFileCellMeta[]
    download?: boolean
    batchDownloadAction?: string
    batchDownloadName?: string
  }>(),
  {
    download: true,
  },
)

const ns = useNamespace('grid')
const { t } = useLocale()

const showBatchDownload = computed(() => {
  if (!props.download || props.files.length < 2) return false
  if (props.batchDownloadAction) return true
  return props.files.some((file) => !!file.url)
})

function displayName(file: GridFileCellMeta) {
  return resolveFileCellDisplayName(file.id, file)
}

function canDownload(file: GridFileCellMeta) {
  return isGridFileDownloadable(file, props.download)
}

function handleDownload(file: GridFileCellMeta) {
  downloadGridFile(file)
}

function handleBatchDownload() {
  downloadGridFilesBatch(
    props.files,
    props.batchDownloadAction,
    props.batchDownloadName,
  )
}
</script>
